import { useState } from 'react'
import { useSupabaseQuery, useSupabaseMutation } from '../../hooks/useSupabase'
import Modal from '../shared/Modal'
import AccountForm from './AccountForm'
import ProviderConfigForm from './ProviderConfigForm'

// Keys inside JsonConfig that must never be rendered in clear on the Accounts page.
const SECRET_KEYS = ['ApiKey', 'ApiSecret', 'AuthToken', 'Password']

function maskSecrets(jsonConfig) {
  if (!jsonConfig || typeof jsonConfig !== 'object') return jsonConfig
  return Object.fromEntries(
    Object.entries(jsonConfig).map(([key, value]) => {
      if (SECRET_KEYS.includes(key) && typeof value === 'string' && value.length > 0) {
        const tail = value.length > 4 ? value.slice(-4) : ''
        return [key, `••••••••${tail}`]
      }
      return [key, value]
    })
  )
}

export default function AccountsTab({ selectedOrganization, selectedAccount, onSelectAccount }) {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingAccount, setEditingAccount] = useState(null)
  const [deletingAccount, setDeletingAccount] = useState(null)

  // Provider-config modals opened from this page.
  const [viewingConfig, setViewingConfig] = useState(null)
  const [editingConfig, setEditingConfig] = useState(null)
  const [creatingConfig, setCreatingConfig] = useState(false)
  // Id of a config created while an account form was open; the form selects it.
  const [pendingConfigId, setPendingConfigId] = useState(null)

  const { data: accounts, loading, error, refetch } = useSupabaseQuery('SmsAccounts', {
    eq: { IsDeleted: false, ...(selectedOrganization && { SmsOrganizationId: selectedOrganization.SmsOrganizationId }) },
    order: { column: 'Name', ascending: true }
  })

  const { data: providerConfigs, refetch: refetchConfigs } = useSupabaseQuery('SmsProviderConfigs', {
    order: { column: 'Name', ascending: true }
  })

  const { data: providers } = useSupabaseQuery('SmsProviders', {
    order: { column: 'SmsProviderName', ascending: true }
  })

  const { insert, update, softDelete, loading: mutating } = useSupabaseMutation('SmsAccounts')
  const configMutation = useSupabaseMutation('SmsProviderConfigs')

  const handleCreate = async (formData) => {
    const { error } = await insert({
      ...formData,
      SmsOrganizationId: selectedOrganization.SmsOrganizationId
    })
    if (!error) {
      setShowCreateModal(false)
      setPendingConfigId(null)
      refetch()
    }
  }

  const handleUpdate = async (formData) => {
    const { error } = await update(editingAccount.SmsAccountId, formData, 'SmsAccountId')
    if (!error) {
      setEditingAccount(null)
      setPendingConfigId(null)
      refetch()
      if (selectedAccount?.SmsAccountId === editingAccount.SmsAccountId) {
        onSelectAccount({ ...selectedAccount, ...formData })
      }
    }
  }

  const handleDelete = async () => {
    const { error } = await softDelete(deletingAccount.SmsAccountId, 'SmsAccountId')
    if (!error) {
      setDeletingAccount(null)
      refetch()
      if (selectedAccount?.SmsAccountId === deletingAccount.SmsAccountId) {
        onSelectAccount(null)
      }
    }
  }

  const handleCreateConfig = async (payload) => {
    const { data, error } = await configMutation.insert(payload)
    if (!error) {
      setCreatingConfig(false)
      setPendingConfigId(data?.SmsProviderConfigId ?? null)
      refetchConfigs()
    }
  }

  const handleUpdateConfig = async (payload) => {
    const { error } = await configMutation.update(editingConfig.SmsProviderConfigId, payload, 'SmsProviderConfigId')
    if (!error) {
      setEditingConfig(null)
      setViewingConfig(null)
      refetchConfigs()
    }
  }

  const closeAccountModals = () => {
    setShowCreateModal(false)
    setEditingAccount(null)
    setPendingConfigId(null)
  }

  const configFormProps = {
    providerConfigs,
    onViewConfig: setViewingConfig,
    onEditConfig: setEditingConfig,
    onCreateConfig: () => setCreatingConfig(true),
    pendingConfigId
  }

  if (!selectedOrganization) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No Organization Selected</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Please select an organization from the Organizations tab first
        </p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  const viewingProvider = viewingConfig && providers?.find(p => p.SmsProviderId === viewingConfig.SmsProviderId)
  const accountsUsingViewingConfig = viewingConfig
    ? accounts?.filter(a => a.SmsProviderConfigId === viewingConfig.SmsProviderConfigId) ?? []
    : []

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">SMS Accounts</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Accounts for: {selectedOrganization.Name}
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>New Account</span>
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          Failed to load accounts: {error.message}
        </div>
      )}

      {/* Accounts Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Provider Config
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {accounts?.map((account) => {
              const config = providerConfigs?.find(c => c.SmsProviderConfigId === account.SmsProviderConfigId)
              const isSelected = selectedAccount?.SmsAccountId === account.SmsAccountId

              return (
                <tr
                  key={account.SmsAccountId}
                  onClick={() => onSelectAccount(account)}
                  className={`cursor-pointer transition-colors ${
                    isSelected
                      ? 'bg-primary-50 dark:bg-primary-900/20'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {isSelected && (
                        <svg className="w-5 h-5 text-primary-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {account.Name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          ID: {account.SmsAccountId}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {config ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          setViewingConfig(config)
                        }}
                        className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded text-xs hover:bg-blue-200 dark:hover:bg-blue-900/50 transition underline-offset-2 hover:underline"
                        title="View provider configuration"
                      >
                        {config.Name}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          setEditingAccount(account)
                        }}
                        className="text-gray-400 italic hover:text-blue-600 dark:hover:text-blue-400 hover:underline"
                        title="Assign a provider configuration"
                      >
                        No config — assign
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200">
                      Active
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setEditingAccount(account)
                      }}
                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setDeletingAccount(account)
                      }}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {(!accounts || accounts.length === 0) && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No accounts</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating a new SMS account.</p>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <Modal title="Create SMS Account" onClose={closeAccountModals}>
          <AccountForm
            {...configFormProps}
            onSubmit={handleCreate}
            onCancel={closeAccountModals}
            loading={mutating}
          />
        </Modal>
      )}

      {/* Edit Modal */}
      {editingAccount && (
        <Modal title="Edit SMS Account" onClose={closeAccountModals}>
          <AccountForm
            {...configFormProps}
            account={editingAccount}
            onSubmit={handleUpdate}
            onCancel={closeAccountModals}
            loading={mutating}
          />
        </Modal>
      )}

      {/* Delete Confirmation */}
      {deletingAccount && (
        <Modal title="Delete SMS Account" onClose={() => setDeletingAccount(null)} size="sm">
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">
              Are you sure you want to delete <strong>{deletingAccount.Name}</strong>?
              This will affect all associated API credentials and templates.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeletingAccount(null)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={mutating}
                className="px-4 py-2 text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {mutating ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Provider Config: View (secrets masked) */}
      {viewingConfig && (
        <Modal title={`Provider Configuration: ${viewingConfig.Name}`} onClose={() => setViewingConfig(null)} size="lg">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Provider</div>
                <div className="text-sm text-gray-900 dark:text-white">
                  {viewingProvider?.SmsProviderName || <span className="italic text-gray-400">Unknown</span>}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Base URL</div>
                <div className="text-sm text-gray-900 dark:text-white truncate">
                  {viewingConfig.JsonConfig?.BaseUrl || <span className="italic text-gray-400">Not set</span>}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Config ID</div>
                <div className="text-sm text-gray-900 dark:text-white">{viewingConfig.SmsProviderConfigId}</div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-900 rounded p-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                JSON Configuration <span className="font-normal text-xs text-gray-500">(secrets masked)</span>
              </h4>
              <pre className="text-xs text-gray-800 dark:text-gray-200 overflow-x-auto">
                {JSON.stringify(maskSecrets(viewingConfig.JsonConfig), null, 2)}
              </pre>
            </div>

            <div className="text-xs text-gray-500 dark:text-gray-400">
              Used by {accountsUsingViewingConfig.length} account{accountsUsingViewingConfig.length === 1 ? '' : 's'} in this organization
              {accountsUsingViewingConfig.length > 0 && `: ${accountsUsingViewingConfig.map(a => a.Name).join(', ')}`}
            </div>

            <div className="flex justify-end space-x-3 pt-2 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setViewingConfig(null)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => setEditingConfig(viewingConfig)}
                className="px-4 py-2 text-white bg-primary-500 rounded-lg hover:bg-primary-600"
              >
                Edit Configuration
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Provider Config: Edit */}
      {editingConfig && (
        <Modal title={`Edit Provider Configuration: ${editingConfig.Name}`} onClose={() => setEditingConfig(null)}>
          <ProviderConfigForm
            config={editingConfig}
            providers={providers}
            onSubmit={handleUpdateConfig}
            onCancel={() => setEditingConfig(null)}
            loading={configMutation.loading}
          />
        </Modal>
      )}

      {/* Provider Config: Create */}
      {creatingConfig && (
        <Modal title="New Provider Configuration" onClose={() => setCreatingConfig(false)}>
          <ProviderConfigForm
            providers={providers}
            onSubmit={handleCreateConfig}
            onCancel={() => setCreatingConfig(false)}
            loading={configMutation.loading}
          />
        </Modal>
      )}
    </div>
  )
}
